// CAT-1C (Reports V2): presentation-only formatting for the student Reports
// page. Pure functions, no store, no i18n instance -- the caller passes the
// locale and the "today" token so the view stays the single owner of copy.
//
// The rendered V2 reference shows counts in locale digits («۱۸») and card
// times as «امروز، ۱۲:۴۷» / «۱۸ مرداد، ۱۷:۲۰» (Persian calendar, day + month
// name, 24-hour clock). `Intl` with `fa-IR` yields exactly that; the Gregorian
// year is appended only when it differs from the current one so an old card
// is never mistaken for a recent one.

const INTL_LOCALE = { fa: 'fa-IR', en: 'en-US' }

function intlLocale(locale) {
  return INTL_LOCALE[locale] || INTL_LOCALE.en
}

/**
 * A count in the locale's digits. Only a real number is formatted: anything
 * else (null = not observed) comes back as an empty string so a caller can
 * never turn "unknown" into "0" by accident.
 * @param {*} value
 * @param {'fa'|'en'} locale
 * @returns {string}
 */
export function formatReportCount(value, locale) {
  if (typeof value !== 'number' || Number.isNaN(value)) return ''
  return value.toLocaleString(intlLocale(locale), { useGrouping: false })
}

function sameCalendarDay(a, b, locale) {
  const f = new Intl.DateTimeFormat(intlLocale(locale), {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  })
  return f.format(a) === f.format(b)
}

/**
 * The V2 card timestamp: «<today>، HH:MM» for a same-day value, otherwise
 * «D MMMM، HH:MM» (plus the year when it is not the current one).
 * @param {string|null|undefined} iso
 * @param {{locale:'fa'|'en', today:string, now?:Date}} options
 * @returns {string} empty for a missing or invalid timestamp
 */
export function formatReportTime(iso, { locale, today, now = new Date() }) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const tag = intlLocale(locale)
  const time = new Intl.DateTimeFormat(tag, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)
  const separator = locale === 'fa' ? '، ' : ', '
  if (sameCalendarDay(date, now, locale)) return `${today}${separator}${time}`
  const sameYear =
    new Intl.DateTimeFormat(tag, { year: 'numeric' }).format(date) ===
    new Intl.DateTimeFormat(tag, { year: 'numeric' }).format(now)
  const day = new Intl.DateTimeFormat(
    tag,
    sameYear
      ? { day: 'numeric', month: 'long' }
      : { day: 'numeric', month: 'long', year: 'numeric' }
  ).format(date)
  return `${day}${separator}${time}`
}
